<!-- src/components/ParticleScene.vue -->

<template>
  <div ref="sceneContainer" class="canvas-container"></div>
  <div
    v-if="tooltip.visible"
    class="tooltip"
    :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }"
  >
    {{ tooltip.content }}
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { gsap } from "gsap";

// Принимаем свойства формы и массива произведений
const props = defineProps({
  shape: { type: String, default: "random" },
  artworks: { type: Array, required: true },
});

const sceneContainer = ref(null);

let scene, camera, renderer, controls, animationId;
const sprites = [];
const targetPositions = [];

// Состояние для отображения подсказки
const tooltip = ref({ visible: false, content: "", x: 0, y: 0 });

// Raycaster для обнаружения пересечений
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

// Инициализация сцены
const initScene = async () => {
  scene = new THREE.Scene();

  const aspectRatio = window.innerWidth / window.innerHeight;
  camera = new THREE.PerspectiveCamera(50, aspectRatio, 0.1, 2000); // Уменьшено FOV для более близкого обзора

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  sceneContainer.value.appendChild(renderer.domElement);

  // Настройка OrbitControls
  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.1;
  controls.screenSpacePanning = false;
  controls.minDistance = 10; // Уменьшена минимальная дистанция для ближнего обзора
  controls.maxDistance = 300; // Уменьшен максимальный диапазон

  // Обработчики событий
  window.addEventListener("resize", onWindowResize);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("touchstart", onTouchStart);
  window.addEventListener("touchmove", onTouchMove);

  // Создание спрайтов
  await createSprites();
  updateTargetPositions(props.shape);

  // Настройка камеры после создания спрайтов
  adjustCamera(true);

  // Анимация спрайтов к целевым позициям
  animateToTargetPositions();
};

// Обработчик изменения размера окна
const onWindowResize = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  renderer.setSize(width, height);
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
};

// Обработчики сенсорных событий
const onTouchStart = (event) => {
  controls.handleTouchStart(event);
};

const onTouchMove = (event) => {
  controls.handleTouchMove(event);
};

// Обработчик движения мыши для подсказок
const onMouseMove = (event) => {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(sprites);

  if (intersects.length > 0) {
    const intersected = intersects[0].object;
    const data = intersected.userData;
    tooltip.value = {
      visible: true,
      content: `Название: ${data.title}, Год: ${data.year}, Автор: ${data.artist}, Период: ${data.period}`,
      x: event.clientX,
      y: event.clientY,
    };
  } else {
    tooltip.value.visible = false;
  }
};

// Функция для создания спрайтов
const createSprites = async () => {
  // Очистка существующих спрайтов
  sprites.forEach((sprite) => {
    scene.remove(sprite);
    if (sprite.material.map) sprite.material.map.dispose();
    sprite.material.dispose();
  });
  sprites.length = 0;

  const loader = new THREE.TextureLoader();

  const loadTexture = (url) => {
    return new Promise((resolve, reject) => {
      loader.load(
        url,
        (texture) => resolve(texture),
        undefined,
        (err) => reject(err)
      );
    });
  };

  const loadPromises = props.artworks.map(async (artwork) => {
    try {
      const texture = await loadTexture(artwork.url);
      const aspectRatio = texture.image.width / texture.image.height;
      const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
      const sprite = new THREE.Sprite(spriteMaterial);

      const scale = 5; // Настроен масштаб
      sprite.scale.set(scale * aspectRatio, scale, scale);

      // Установка начальной позиции всех спрайтов в центр сцены
      sprite.position.set(0, 0, 0);

      // Хранение данных произведения в спрайте
      sprite.userData = {
        id: artwork.id,
        title: artwork.title,
        year: artwork.year,
        artist: artwork.artist,
        period: artwork.period,
      };

      sprites.push(sprite);
      scene.add(sprite);
    } catch (err) {
      console.error(`Ошибка загрузки текстуры ${artwork.url}:`, err);
    }
  });

  await Promise.all(loadPromises);
};

// Функция для получения позиций в режиме "линия", центрированной по оси X
const getPositionsByYear = () => {
  const positions = [];
  const artworks = props.artworks.slice(); // Копирование массива

  // Сортировка произведений по году
  artworks.sort((a, b) => a.year - b.year);

  const minSpacing = 5; // Минимальное расстояние между произведениями по оси X
  let currentX = 0;

  for (let i = 0; i < artworks.length; i++) {
    const artwork = artworks[i];
    let xPos;

    if (i === 0) {
      xPos = 0;
    } else {
      const prevArtwork = artworks[i - 1];
      const yearDiff = artwork.year - prevArtwork.year;

      if (yearDiff > 0) {
        xPos = currentX + minSpacing + yearDiff * 0.5;
      } else {
        xPos = currentX + minSpacing;
      }
    }

    positions.push({
      artworkId: artwork.id,
      position: [xPos, 0, 0],
    });

    currentX = xPos;
  }

  // Центрирование линии по оси X
  const totalLength = currentX;
  const offsetX = totalLength / 2;
  positions.forEach((item) => {
    item.position[0] -= offsetX;
  });

  return positions;
};

// Функция для обновления целевых позиций спрайтов на основе формы
const updateTargetPositions = (shape) => {
  targetPositions.length = 0;

  if (shape === "line") {
    const positionsByYear = getPositionsByYear();
    // Создание мапы id произведения к позиции
    const idToPosition = {};
    positionsByYear.forEach((item) => {
      idToPosition[item.artworkId] = item.position;
    });

    // Обновление targetPositions в соответствии с порядком спрайтов
    sprites.forEach((sprite) => {
      const position = idToPosition[sprite.userData.id];
      targetPositions.push(position);
    });
  } else {
    const count = sprites.length;
    for (let i = 0; i < count; i++) {
      const target = getShapePosition(shape, i, count);
      targetPositions.push(target);
    }
  }
};

// Функция для получения позиции спрайта на основе формы
const getShapePosition = (shape, i, count) => {
  switch (shape) {
    case "sphere": {
      const phi = Math.acos(-1 + (2 * i) / count);
      const theta = Math.sqrt(count * Math.PI) * phi;
      const radius = 20; // Уменьшенный радиус сферы
      return [
        Math.cos(theta) * Math.sin(phi) * radius,
        Math.sin(theta) * Math.sin(phi) * radius,
        Math.cos(phi) * radius,
      ];
    }
    case "random": {
      // Генерация позиций внутри сферы радиуса 20
      const maxRadius = 20; // Уменьшенный максимальный радиус для случайного распределения

      // Генерация случайного радиуса с кубическим распределением для большей плотности в центре
      const rand = Math.random();
      const radius = Math.cbrt(rand) * maxRadius;

      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);

      return [
        radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.sin(phi) * Math.sin(theta),
        radius * Math.cos(phi),
      ];
    }
    default: {
      // "random" по умолчанию
      const radius = 50; // Максимальный радиус для случайного расположения
      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);
      return [
        radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.sin(phi) * Math.sin(theta),
        radius * Math.cos(phi),
      ];
    }
  }
};

// Функция для настройки камеры с возможной анимацией при инициализации
const adjustCamera = (initial = false) => {
  // Вычисление границ всех спрайтов
  const boundingBox = new THREE.Box3();
  sprites.forEach((sprite) => {
    boundingBox.expandByObject(sprite);
  });

  const center = new THREE.Vector3();
  boundingBox.getCenter(center);

  const size = new THREE.Vector3();
  boundingBox.getSize(size);

  // Определение позиции камеры на основе размера сцены и FOV
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  let cameraZ = Math.abs(maxDim / (2 * Math.tan(fov / 2)));

  cameraZ *= 0.8; // Уменьшенный коэффициент для ближнего обзора

  const newCameraPosition = new THREE.Vector3(center.x, center.y, cameraZ);

  if (initial) {
    // Установка начальной позиции камеры для эффекта пролёта
    camera.position.set(center.x, center.y + 50, cameraZ + 300); // Увеличено отступление камеры

    // Анимация камеры к новой позиции
    gsap.to(camera.position, {
      x: newCameraPosition.x,
      y: newCameraPosition.y,
      z: newCameraPosition.z,
      duration: 3,
      ease: "power2.inOut",
    });

    // Анимация цели контроллеров на центр сцены
    gsap.to(controls.target, {
      x: center.x,
      y: center.y,
      z: center.z,
      duration: 3,
      ease: "power2.inOut",
      onUpdate: () => {
        controls.update();
      },
    });
  } else {
    // При смене формы камера будет возвращаться к центру через отдельную функцию
  }

  camera.near = maxDim / 100;
  camera.far = maxDim * 100;
  camera.updateProjectionMatrix();
};

// Функция для плавного возвращения камеры к центру сцены
const resetCameraToCenter = () => {
  // Вычисление границ всех спрайтов
  const boundingBox = new THREE.Box3();
  sprites.forEach((sprite) => {
    boundingBox.expandByObject(sprite);
  });

  const center = new THREE.Vector3();
  boundingBox.getCenter(center);

  const size = new THREE.Vector3();
  boundingBox.getSize(size);

  // Определение позиции камеры на основе размера сцены и FOV
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  let cameraZ = Math.abs(maxDim / (2 * Math.tan(fov / 2)));

  cameraZ *= 0.8; // Уменьшенный коэффициент для ближнего обзора

  const newCameraPosition = new THREE.Vector3(center.x, center.y, cameraZ);

  // Плавная анимация камеры к новой позиции
  gsap.to(camera.position, {
    x: newCameraPosition.x,
    y: newCameraPosition.y,
    z: newCameraPosition.z,
    duration: 1,
    ease: "power2.out",
  });

  // Плавная анимация цели контроллеров к центру сцены
  gsap.to(controls.target, {
    x: center.x,
    y: center.y,
    z: center.z,
    duration: 1,
    ease: "power2.out",
    onUpdate: () => {
      controls.update();
    },
  });
};

// Функция для анимации спрайтов к их целевым позициям
const animateToTargetPositions = () => {
  sprites.forEach((sprite, i) => {
    const target = targetPositions[i];
    if (target) {
      gsap.to(sprite.position, {
        x: target[0],
        y: target[1],
        z: target[2],
        duration: 1,
        ease: "power2.out",
      });
    }
  });
};

// Цикл анимации
const animate = () => {
  animationId = requestAnimationFrame(animate);

  controls.update();
  renderer.render(scene, camera);
};

// Хуки жизненного цикла
onMounted(async () => {
  await initScene();
  animate();
});

onUnmounted(() => {
  cancelAnimationFrame(animationId);
  window.removeEventListener("resize", onWindowResize);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("touchstart", onTouchStart);
  window.removeEventListener("touchmove", onTouchMove);

  sprites.forEach((sprite) => {
    if (sprite.material.map) sprite.material.map.dispose();
    sprite.material.dispose();
    scene.remove(sprite);
  });
  renderer.dispose();
});

// Наблюдатели за изменениями свойств
watch(
  () => props.shape,
  (newShape) => {
    updateTargetPositions(newShape);
    if (newShape === "random") {
      // Плавно сбросить позиции спрайтов в центр
      sprites.forEach((sprite) => {
        gsap.to(sprite.position, {
          x: 0,
          y: 0,
          z: 0,
          duration: 1,
          ease: "power2.out",
        });
      });
      // Затем анимировать к целевым позициям через задержку
      gsap.delayedCall(1, animateToTargetPositions);
    } else {
      // Для других форм просто анимировать к целевым позициям
      animateToTargetPositions();
    }
    resetCameraToCenter(); // Плавно возвращаем камеру к центру
  }
);

watch(
  () => props.artworks,
  async () => {
    await createSprites();
    updateTargetPositions(props.shape);
    animateToTargetPositions();
    adjustCamera(true); // Перенастройка камеры с анимацией при изменении произведений
  }
);
</script>

<style scoped>
.canvas-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  pointer-events: none;
  transform: translate(-50%, -100%);
  white-space: nowrap;
  font-size: 14px;
  z-index: 100;
}
</style>
